import { Extension } from '@tiptap/core'
import type { Editor } from '@tiptap/core'

/** ATX bounds — a heading never leaves the H1..H6 range (Shift+Tab on H1 keeps it a heading). */
const MIN_LEVEL = 1
const MAX_LEVEL = 6

/**
 * Move every top-level heading the selection touches one level down (`delta` = +1, H2 → H3) or
 * up (`delta` = −1), clamped to H1..H6, in ONE transaction — one undo step, one Y.Text flush,
 * one history entry.
 *
 * Acts only when the selection HEAD sits on a top-level heading (the same "top-level only" rule
 * as pm-sections.ts#pmHeadings, so the § numbers and outline follow the new levels). Otherwise it
 * returns false and Tab keeps its usual meaning: list indent, next table cell, etc. Non-heading
 * blocks inside a range selection are left untouched.
 *
 * Returns true whenever it acted on headings — even when every one was already at the bound — so
 * the key never moves focus out of the editor. A level change inside a § someone else holds is
 * rejected by SectionNumbers' filterTransaction (which toasts), not here.
 */
export function shiftHeadingLevels(editor: Editor, delta: 1 | -1): boolean {
  if (!editor.isEditable) return false
  const { state, view } = editor
  const { selection, doc } = state
  const $head = selection.$head
  if ($head.depth !== 1 || $head.parent.type.name !== 'heading') return false

  const { from, to } = selection
  const tr = state.tr
  doc.forEach((node, offset) => {
    if (node.type.name !== 'heading') return
    // A top-level block the selection touches — the caret case reduces to the one heading.
    if (offset + node.nodeSize <= from || offset > to) return
    const level = Number(node.attrs.level) || MIN_LEVEL
    const next = Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, level + delta))
    if (next !== level) tr.setNodeMarkup(offset, undefined, { ...node.attrs, level: next })
  })
  if (tr.docChanged) view.dispatch(tr.scrollIntoView())
  return true
}

/**
 * Tab / Shift+Tab change the heading level (AB#319). Ranked above the stock keymaps (priority
 * 100: list items, table cells) so it sees the key first, and steps aside — returns false —
 * whenever the caret is not on a heading.
 */
export const HeadingLevelKeys = Extension.create({
  name: 'headingLevelKeys',
  priority: 1000,

  addKeyboardShortcuts() {
    return {
      Tab: () => shiftHeadingLevels(this.editor, 1),
      'Shift-Tab': () => shiftHeadingLevels(this.editor, -1),
    }
  },
})
